import type { ApiErrorHandlingOptions } from "../apiRequestOptions";
import { makeCopyErrorDetailsAction } from "./errorDetails";
import { normalizeError } from "./normalizeError";
import { getGlobalNotifier } from "./notifier";
import { withSentryEventIdDescription } from "./sentryEventDescription";
import { makeSentryFeedbackNotifyAction } from "./sentryFeedback";
import { resolveUserFacingError } from "./userFacingErrors";

export type NotifyUserFacingErrorOptions = ApiErrorHandlingOptions &
  Readonly<{
    /** Operation name for errors that do not carry one themselves. */
    operation?: string;
  }>;

/**
 * Shows an error to the user through the global notifier: resolved level and
 * text, a key for dedupe and the feedback action. An error with a reference
 * of the backend gets the "copy details" action; one without shows the
 * Fehler-ID of the backend event. Does nothing for `showErrorToast: false`.
 *
 * The options may grow; the normalised error is resolved once here.
 */
export function notifyUserFacingError(
  error: unknown,
  options?: NotifyUserFacingErrorOptions,
): void {
  if (options?.showErrorToast === false) {
    return;
  }

  const normalized = normalizeError(error, { operation: options?.operation });
  const formatted = resolveUserFacingError(normalized, {
    defaultMessage: options?.defaultMessage,
    keyPrefix: options?.keyPrefix,
    operationContext: options?.operationContext,
  });
  const copyAction = makeCopyErrorDetailsAction(normalized);
  getGlobalNotifier().notify(formatted.level, formatted.message, {
    // A stable key keeps repeated failures from spamming the user.
    key: formatted.key,
    // The reference finds the backend event, so its id is not shown as well;
    // it is part of the copied details.
    description: copyAction
      ? formatted.description
      : withSentryEventIdDescription(
          formatted.description,
          normalized.sentryEventId,
        ),
    action: makeSentryFeedbackNotifyAction({
      associatedEventId: normalized.sentryEventId,
      source: "graphql-error-toast",
      title: formatted.message,
    }),
    ...(copyAction ? { extraActions: [copyAction] } : undefined),
  });
}

/**
 * The toast of a failed API request (generated call or subscription), with
 * the defaults both transports share.
 * @internal
 */
export function notifyApiRequestError(
  error: unknown,
  options?: NotifyUserFacingErrorOptions,
): void {
  notifyUserFacingError(error, {
    ...options,
    keyPrefix: options?.keyPrefix ?? "api",
  });
}
